/* InsightsFilterRequestDto.ts */

/* Utils */
import {
  transformTimePeriod,
  transformToDefaultCohort,
} from "src/utils/helper/insights-filter/insights-filter";

/* Interfaces */
import {
  ApiBaseInsightsFilterRequest,
  BaseInsightsFilterRequest,
  SurvivalAnalysisFilterRequest,
  TransformedSurvivalAnalysisRequest,
} from "src/shared/interfaces/api/InsightsFilters";

import {
  TreatmentPatternsInsightsFilterRequest,
  ApiTreatmentPatternsInsightsFilterRequest,
} from "src/shared/interfaces/api/TreatmentPatterns";

export class InsightsFilterRequestDto {
  /**
   * Transform insights filter to `ApiBaseInsightsFilterRequest`.
   *
   * @param {BaseInsightsFilterRequest} insightsFilter
   * @returns {ApiBaseInsightsFilterRequest}
   */
  static toBaseInsightsFilterRequest(
    insightsFilter: BaseInsightsFilterRequest
  ): ApiBaseInsightsFilterRequest {
    const {
      mainCondition,
      feature,
      cohort,
      timePeriod,
      customCohortsStr,
      careSiteNames,
    } = insightsFilter;

    const transformedCohort =
      transformToDefaultCohort(cohort);

    const { firstYear, lastYear } = transformTimePeriod(
      timePeriod!
    );

    return {
      main_condition: mainCondition,
      feature,
      cohort: transformedCohort,
      start_date: firstYear,
      end_date: lastYear,
      custom_cohorts_str: customCohortsStr,
      care_site_names: careSiteNames?.length ? careSiteNames : undefined,
    };
  }

  static toSurvivalAnalysisRequest(
    insightsFilter: SurvivalAnalysisFilterRequest
  ): TransformedSurvivalAnalysisRequest {
    const {
      mainCondition,
      cohort,
      timePeriod,
      customCohortsStr,
      startPoint,
      careSiteNames,
    } = insightsFilter;

    const transformedCohort =
      transformToDefaultCohort(cohort);

    const { firstYear, lastYear } = transformTimePeriod(
      timePeriod!
    );

    return {
      main_condition: mainCondition,
      cohort: transformedCohort,
      start_date: firstYear,
      end_date: lastYear,
      survival_analysis_start_point: startPoint,
      custom_cohorts_str: customCohortsStr,
      care_site_names: careSiteNames?.length ? careSiteNames : undefined,
    };
  }

  /**
   * Transform insights filter to `ApiTreatmentPatternsInsightsFilterRequest`.
   *
   * @param {TreatmentPatternsInsightsFilterRequest} insightsFilter
   * @returns {ApiTreatmentPatternsInsightsFilterRequest}
   */
  static toTreatmentPatternsInsightsFilterRequest(
    insightsFilter: TreatmentPatternsInsightsFilterRequest
  ): ApiTreatmentPatternsInsightsFilterRequest {
    const {
      mainCondition,
      cohort,
      timePeriod,
      customCohortsStr,
      customFeature,
      maxDepth,
      includeNoValueStates,
      careSiteNames,
    } = insightsFilter;

    const transformedCohort =
      transformToDefaultCohort(cohort);

    const { firstYear, lastYear } = transformTimePeriod(
      timePeriod!
    );

    return {
      main_condition: mainCondition,
      cohort: transformedCohort,
      start_date: firstYear,
      end_date: lastYear,
      max_depth: maxDepth,
      feature_name: customFeature,
      custom_cohorts_str: customCohortsStr,
      // A custom cohort has no valid completeness denominator, so never request
      // no-value states for one - the UI only disables the toggle visually, so
      // a previously-enabled value could otherwise still be sent here.
      include_no_value_states: includeNoValueStates && !customCohortsStr,
      care_site_names: careSiteNames?.length ? careSiteNames : undefined,
    };
  }
}
